// Mail-merge token validation for broadcast composer fields.
//
// Tokens are case-insensitive `{{name}}` placeholders. The backend renderer
// (`backend-gohire-v2/lib/message.js:constructMessage`) lowercases the inner
// name and then resolves it in two layers:
//   1. Per-contact `contactdetail.field` row (firstname, lastname, custom
//      CSV-import fields, etc.) — content varies per recipient.
//   2. `CTX_SYNONYMS` fallback (user.name / company.name / campaign.name).
//
// The whitelist below covers (a) firstname/lastname which are populated for
// every contact on creation, and (b) every CTX_SYNONYMS key. Anything outside
// is either a per-contact custom field (which the backend may resolve for
// some recipients but not others) OR a typo that the backend silently strips
// to empty string. We flag both so the user sees the issue before the
// campaign goes out.

export const VALID_MERGE_TOKENS = new Set([
    // Always-populated per-contact fields
    'firstname',
    'lastname',
    // Recruiter / sender (CTX_SYNONYMS → user.name)
    'recruiter',
    'recruiter_name',
    'recruitername',
    // Recruiter email (CTX_SYNONYMS → user.email)
    'recruiter_email',
    'recruiteremail',
    // Brand / company (CTX_SYNONYMS → company.name)
    'brand',
    'brand_name',
    'brandname',
    'company',
    'company_name',
    'companyname',
    // Campaign (CTX_SYNONYMS → campaign.name)
    'campaign_name',
    'campaignname',
    // Extension-populated per-contact fields (Chrome extension Workday scrape
    // writes these contactdetail rows; see gohire-extension-vite
    // src/utils/scraped-tags.ts). Whitelisted so templates and sequences can
    // be authored before any contact has the field yet.
    'workday_position',
    'workday_req_id',
]);

const TOKEN_RE = /{{([\s\S]+?)}}/g;
const INVALID_CHAR_RE = /[^a-zA-Z0-9_]/;

// `extraValidTokens` lets callers extend the static whitelist with
// company-specific custom contact-field names (fetched from
// /api/v3/contacts/custom-fields). Pass as an iterable of lowercase strings.
export function findInvalidMergeTokens(text, extraValidTokens = null) {
    if (!text || typeof text !== 'string') return [];
    const seen = new Set();
    const invalid = [];
    const extra = extraValidTokens
        ? (extraValidTokens instanceof Set ? extraValidTokens : new Set(extraValidTokens))
        : null;
    let match;
    // Reset lastIndex each call since TOKEN_RE is module-scoped.
    TOKEN_RE.lastIndex = 0;
    while ((match = TOKEN_RE.exec(text)) !== null) {
        const raw = match[0];
        if (seen.has(raw)) continue;
        seen.add(raw);
        const inner = match[1];
        // Backend lowercases inside {{...}} before lookup, but rejects any
        // whitespace or non-[a-z0-9_] character. Match that exactly.
        if (
            inner.length === 0
            || inner !== inner.trim()
            || INVALID_CHAR_RE.test(inner)
        ) {
            invalid.push(raw);
            continue;
        }
        const lower = inner.toLowerCase();
        if (!VALID_MERGE_TOKENS.has(lower) && !(extra && extra.has(lower))) {
            invalid.push(raw);
        }
    }
    return invalid;
}

export function formatInvalidTokenMessage(tokens, max = 5) {
    if (!Array.isArray(tokens) || tokens.length === 0) return null;
    const shown = tokens.slice(0, max).join(', ');
    const overflow = tokens.length > max ? ` (+${tokens.length - max} more)` : '';
    return `Unknown merge fields: ${shown}${overflow}`;
}
